import {WSPIconName, WSPIconSize} from '@mintdata-wsp/resources/icons/WSPIcons';
import * as React from 'react';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import bind from 'bind-decorator';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {ToolbarButton, TooltipSide} from '@mintdata-wsp/wsp/ui/components/general/ToolbarButton';
import {observer} from 'mobx-react';

const styles = require('./Toolbar.less');
const cx = classnames.bind(styles);

export interface ToolbarPopup
{
    toolbarItem:ToolbarItem;
}

export enum ToolbarButtonBehavior
{
    Button,
    ToggleOnOff
}

export class ToolbarItem
{
    public readonly id:GUID = IdUtil.newGUID();

    @observable
    public isToggledOn:boolean = false;

    private constructor(public readonly iconName:WSPIconName,
                        public readonly iconSize:WSPIconSize,
                        public readonly buttonText:string,
                        public readonly behavior:ToolbarButtonBehavior,
                        public readonly tooltip:string,
                        public readonly tooltipSide:TooltipSide,
                        public readonly popupContent:React.ReactElement<ToolbarPopup>|null)
    {}

    public isEqualTo(other:ToolbarItem):boolean
    {
        return other && other.id === this.id;
    }

    public static button(iconName:WSPIconName,
                         tooltip:string=null,
                         tooltipSide:TooltipSide=TooltipSide.North):ToolbarItem
    {
        return new ToolbarItem(iconName, WSPIconSize.medium24, null, ToolbarButtonBehavior.Button, tooltip, tooltipSide, null);
    }

    public static toggleButton(iconName:WSPIconName,
                               tooltip:string=null,
                               tooltipSide:TooltipSide=TooltipSide.North,
                               buttonText:string=null):ToolbarItem
    {
        return new ToolbarItem(iconName, WSPIconSize.medium24, buttonText, ToolbarButtonBehavior.ToggleOnOff, tooltip, tooltipSide, null);
    }

    public static popup(iconName:WSPIconName,
                        tooltip:string,
                        tooltipSide:TooltipSide=TooltipSide.North,
                        popupContent:React.ReactElement<ToolbarPopup>):ToolbarItem
    {
        return new ToolbarItem(iconName, WSPIconSize.medium24, null, ToolbarButtonBehavior.Button, tooltip, tooltipSide, popupContent);
    }
}

export type OnToolbarClick = (item:ToolbarItem)=>(void);

export interface ToolbarProps
{
    items:ToolbarItem[];
    horizontalSpacing?:number;
    className?:string;
    onClick?:OnToolbarClick;
    isDisableToggleControl?:boolean;
}

@observer
export class Toolbar extends React.Component<ToolbarProps, any>
{
    @bind
    private _onClick(item:ToolbarItem)
    {
        return action((e:React.SyntheticEvent<any>)=>
        {
            const {onClick, isDisableToggleControl=false} = this.props;

            if( !isDisableToggleControl )
            {
                if( item.behavior === ToolbarButtonBehavior.ToggleOnOff )
                    item.isToggledOn = !item.isToggledOn;
            }

            if( onClick && typeof onClick === 'function' )
                onClick(item);
        });
    }

    @bind
    private _renderItem(item:ToolbarItem, itemIndex:number, numItems:number)
    {
        var button =  <ToolbarButton key={item.id}
                               iconName={item.iconName}
                               onClick={this._onClick(item)}
                               tooltip={item.tooltip}
                               tooltipSide={item.tooltipSide}
                               popupContent={item.popupContent}
                               className={cx('toolbarButton')}
                               behavior={item.behavior}
                               isToggledOn={item.behavior === ToolbarButtonBehavior.ToggleOnOff && item.isToggledOn}
                />;

        let dividerStyle:any = null;

        const {horizontalSpacing} = this.props;
        if( horizontalSpacing && (itemIndex < numItems-1) )
        {
            dividerStyle = {};
            dividerStyle.marginRight = horizontalSpacing;
        }

        const spacer = <div key={item.id + '-divider'} style={dividerStyle}/>;

        return [button, spacer];
    }

    render()
    {
        let {className, items = []} = this.props;

        return  <div className={cx('toolbar', className)}>
                {
                    items.map((item:ToolbarItem, idx)=>
                    {
                        return this._renderItem(item, idx, items.length);
                    })
                }
                </div>;
    }
}
